import { useState, useCallback } from "react";
import axios from "axios";
import { GlobalCurrencyType } from "ClientContext";

type CurrencyResponse = GlobalCurrencyType;

interface UseCurrencyProps {
  internal_ID?: string;
  action?: string;
}

const fallbackUSD: GlobalCurrencyType = {
  id: 50,
  code: "USD",
  name: "US dollar",
  euro_forex: 1,
  symbol: "$",
  decimal_places: 2,
};

interface FetchCurrenciesParams {
  currency: string;
  defaultLang: string;
}

if (!process.env.REACT_APP_SERVER_URL) {
  throw new Error("Missing REACT_APP_SERVER_URL in environment");
}
const SERVER_URL = process.env.REACT_APP_SERVER_URL;

export function useCurrency(props?: UseCurrencyProps) {
  // SINGLE object, not an array
  const [currencies, setCurrencies] = useState<CurrencyResponse>(fallbackUSD);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCurrencies = useCallback(
    async ({ currency, defaultLang }: FetchCurrenciesParams) => {
      setError(null);
      setLoading(true);
      try {
        const { data, status } = await axios.post(`${SERVER_URL}/api/controller`, {
          action: props?.action,
          internal_ID: props?.internal_ID,
          params: { default_Lang: defaultLang, currency },
        });

        if (status === 200 && data.status !== "Failed") {
          // Normalize: if API sends array, take first; if object, use it
          const raw = data.currencies ?? data.items ?? data;
          const selected: CurrencyResponse = Array.isArray(raw) ? raw[0] : raw;

          // Optional: sanity defaults
          const safe: CurrencyResponse = {
            id: selected?.id ?? 0,
            code: selected?.code ?? "USD",
            name: selected?.name ?? "US dollar",
            euro_forex: selected?.euro_forex ?? 1,
            symbol: selected?.symbol ?? "$",
            decimal_places: selected?.decimal_places ?? 2,
          };

          setCurrencies(safe);
        } else {
          setError(data.error || data.message || "Failed to fetch currencies");
        }
      } catch (err) {
        setError(
          axios.isAxiosError(err)
            ? err.response?.data?.message || err.message || "Network error"
            : "Unexpected error"
        );
      } finally {
        setLoading(false);
      }
    },
    [props?.action, props?.internal_ID]
  );

  return { currencies, loading, error, fetchCurrencies };
}